import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Form,
  Offcanvas,
  Spinner,
  Table,
} from "react-bootstrap";
import type { User } from "firebase/auth";
import type { OrganizationRecord } from "../../../api/organizations";
import {
  fetchOrganizationActivity,
  type OrganizationActivityReport,
} from "../../../api/organizationActivity";
import {
  clearOrganizationEmailSuppression,
  fetchOrganizationEmailSuppression,
  fetchOrganizationEmails,
  sendOrganizationEmail,
  suppressOrganizationEmail,
  type OrganizationEmailHistoryItem,
} from "../../../api/organizationEmail";
import { formatDaysFromNow, isPlanExpired } from "../utils";
import "./OrganizationActivityDrawer.css";

interface OrganizationActivityDrawerProps {
  show: boolean;
  onHide: () => void;
  org: OrganizationRecord | null;
  user: User;
}

const ENTITY_LABELS: Record<string, string> = {
  products: "Products",
  clients: "Clients",
  suppliers: "Suppliers",
  salesPeople: "Sales people",
  sales: "Sales",
  purchases: "Purchases",
  admins: "Admins",
};

const ENTITY_ORDER = [
  "products",
  "clients",
  "suppliers",
  "salesPeople",
  "sales",
  "purchases",
  "admins",
] as const;

/** Minimum gap between sends (ms). Also enforced server-side. */
const SEND_COOLDOWN_MS = 5000;

function formatDateTime(value?: string | null): string {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  });
}

function formatUsage(current: number, limit?: number | null): string {
  if (typeof limit === "number" && limit > 0) {
    return `${current} / ${limit}`;
  }
  return String(current);
}

function buildDraftFromActivity(
  report: OrganizationActivityReport,
): { subject: string; body: string } {
  const name = report.organizationName || "equipo";
  const products = report.entities.products?.count ?? 0;
  const clients = report.entities.clients?.count ?? 0;
  const sales = report.entities.sales?.count ?? 0;
  const signals = report.agentBaseline.signals;

  let subject = `Seguimiento MayorX — ${name}`;
  let angle =
    "Quería saber cómo va su operación con MayorX y si podemos ayudarles a sacar más valor del sistema.";

  if (!signals.hasProducts) {
    subject = `¿Les ayudamos a cargar su catálogo? — ${name}`;
    angle =
      "Vi que aún no tienen productos cargados. Podemos acompañarlos a importar el catálogo y dejar el inventario listo para vender.";
  } else if (!signals.hasClients) {
    subject = `Siguiente paso: clientes y ventas — ${name}`;
    angle = `Ya tienen ${products} producto${products === 1 ? "" : "s"} cargado${products === 1 ? "" : "s"}. El siguiente paso natural es cargar clientes y cerrar las primeras ventas.`;
  } else if (!signals.hasSales) {
    subject = `Activemos las primeras ventas — ${name}`;
    angle = `Tienen base lista (${products} productos, ${clients} clientes). Podemos ayudarles a cerrar las primeras facturas y a formar al equipo de ventas.`;
  } else {
    angle = `Veo actividad real en su cuenta (${products} productos, ${clients} clientes, ${sales} ventas). Me gustaría proponerles una revisión rápida para optimizar uso y crecimiento.`;
  }

  const body = [
    angle,
    "",
    "Si les parece, agendamos 15 minutos esta semana.",
    "",
    "¿Les funciona?",
  ].join("\n");

  return { subject, body };
}

export const OrganizationActivityDrawer: React.FC<
  OrganizationActivityDrawerProps
> = ({ show, onHide, org, user }) => {
  const [data, setData] = useState<OrganizationActivityReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copiedBaseline, setCopiedBaseline] = useState(false);

  const [recipientEmail, setRecipientEmail] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [sendCooldownUntil, setSendCooldownUntil] = useState(0);
  const [sendCooldownRemainingMs, setSendCooldownRemainingMs] = useState(0);
  const sendInFlightRef = useRef(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const [sendSuccess, setSendSuccess] = useState<string | null>(null);
  const [emailHistory, setEmailHistory] = useState<
    OrganizationEmailHistoryItem[]
  >([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [suppressed, setSuppressed] = useState(false);
  const [suppressionBusy, setSuppressionBusy] = useState(false);

  const loadEmails = useCallback(async () => {
    if (!org) return;
    setHistoryLoading(true);
    try {
      const items = await fetchOrganizationEmails(user, org.id, 15);
      setEmailHistory(items);
    } catch {
      setEmailHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  }, [org, user]);

  const loadSuppression = useCallback(
    async (email?: string) => {
      if (!org) return;
      try {
        const status = await fetchOrganizationEmailSuppression(
          user,
          org.id,
          email,
        );
        setSuppressed(Boolean(status.suppressed));
      } catch {
        setSuppressed(false);
      }
    },
    [org, user],
  );

  useEffect(() => {
    if (!show || !org) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setData(null);
    setCopiedBaseline(false);
    setSendError(null);
    setSendSuccess(null);
    setSending(false);
    sendInFlightRef.current = false;
    setSendCooldownUntil(0);
    setSendCooldownRemainingMs(0);
    setSubject("");
    setBody("");
    setRecipientEmail(org.email ?? "");

    fetchOrganizationActivity(user, org.id, 50)
      .then((report) => {
        if (cancelled) return;
        setData(report);
        const nextEmail = report.email || org.email || "";
        setRecipientEmail(nextEmail);
        void loadSuppression(nextEmail);
      })
      .catch((e) => {
        if (!cancelled) {
          setError(
            e instanceof Error ? e.message : "Failed to load organization activity",
          );
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    loadEmails();

    return () => {
      cancelled = true;
    };
  }, [show, org, user, loadEmails, loadSuppression]);

  useEffect(() => {
    if (!copiedBaseline) return;
    const timer = window.setTimeout(() => setCopiedBaseline(false), 1500);
    return () => window.clearTimeout(timer);
  }, [copiedBaseline]);

  const handleCopyBaseline = async () => {
    if (!data) return;
    const payload = JSON.stringify(data, null, 2);
    try {
      await navigator.clipboard.writeText(payload);
      setCopiedBaseline(true);
    } catch {
      const input = document.createElement("textarea");
      input.value = payload;
      input.setAttribute("readonly", "");
      input.style.position = "absolute";
      input.style.left = "-9999px";
      document.body.appendChild(input);
      input.select();
      document.execCommand("copy");
      document.body.removeChild(input);
      setCopiedBaseline(true);
    }
  };

  const handleDraftFromActivity = () => {
    if (!data) return;
    const draft = buildDraftFromActivity(data);
    setSubject(draft.subject);
    setBody(draft.body);
    setSendError(null);
    setSendSuccess(null);
  };

  useEffect(() => {
    if (sendCooldownUntil <= 0) {
      setSendCooldownRemainingMs(0);
      return;
    }
    const tick = () => {
      const remaining = Math.max(0, sendCooldownUntil - Date.now());
      setSendCooldownRemainingMs(remaining);
      if (remaining <= 0) {
        setSendCooldownUntil(0);
      }
    };
    tick();
    const timer = window.setInterval(tick, 250);
    return () => window.clearInterval(timer);
  }, [sendCooldownUntil]);

  const handleSendEmail = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!org || suppressed || sendInFlightRef.current) return;
    if (Date.now() < sendCooldownUntil) return;

    sendInFlightRef.current = true;
    setSending(true);
    setSendCooldownUntil(Date.now() + SEND_COOLDOWN_MS);
    setSendError(null);
    setSendSuccess(null);
    try {
      const result = await sendOrganizationEmail(user, org.id, {
        subject: subject.trim(),
        body: body.trim(),
        recipientEmail: recipientEmail.trim() || undefined,
        purpose: "marketing",
      });
      setSendSuccess(`Sent to ${result.recipientEmail}`);
      setSubject("");
      setBody("");
      await loadEmails();
    } catch (e) {
      setSendError(e instanceof Error ? e.message : "Failed to send email");
      if (
        e instanceof Error &&
        e.message.toLowerCase().includes("unsubscribed")
      ) {
        setSuppressed(true);
      }
    } finally {
      sendInFlightRef.current = false;
      setSending(false);
    }
  };

  const handleClearSuppression = async () => {
    if (!org) return;
    setSuppressionBusy(true);
    setSendError(null);
    try {
      await clearOrganizationEmailSuppression(
        user,
        org.id,
        recipientEmail.trim() || undefined,
      );
      setSuppressed(false);
      setSendSuccess("Suppression cleared. You can email this address again.");
    } catch (e) {
      setSendError(
        e instanceof Error ? e.message : "Failed to clear suppression",
      );
    } finally {
      setSuppressionBusy(false);
    }
  };

  const handleSuppress = async () => {
    if (!org || !recipientEmail.trim()) return;
    setSuppressionBusy(true);
    setSendError(null);
    try {
      await suppressOrganizationEmail(
        user,
        org.id,
        recipientEmail.trim(),
        "manual",
      );
      setSuppressed(true);
      setSendSuccess("Address suppressed. No more platform emails will be sent.");
    } catch (e) {
      setSendError(
        e instanceof Error ? e.message : "Failed to suppress address",
      );
    } finally {
      setSuppressionBusy(false);
    }
  };

  const signals = data?.agentBaseline.signals ?? {};
  const planExpired = org ? isPlanExpired(org) : false;
  const sendCooldownActive = sendCooldownRemainingMs > 0;
  const canSend =
    Boolean(recipientEmail.trim()) &&
    Boolean(subject.trim()) &&
    Boolean(body.trim()) &&
    !sending &&
    !sendCooldownActive &&
    !suppressed;
  const sendButtonLabel = sending
    ? "Sending…"
    : sendCooldownActive
      ? `Wait ${Math.ceil(sendCooldownRemainingMs / 1000)}s…`
      : "Send email";

  return (
    <Offcanvas
      show={show}
      onHide={onHide}
      placement="end"
      className="org-activity-drawer"
      scroll
    >
      <Offcanvas.Header closeButton>
        <Offcanvas.Title>
          <div className="org-activity-drawer__title">
            {org?.name ?? "Organization activity"}
          </div>
          <div className="org-activity-drawer__subtitle text-muted">
            Activity baseline · outreach for ops &amp; agents
          </div>
        </Offcanvas.Title>
      </Offcanvas.Header>
      <Offcanvas.Body>
        {planExpired ? (
          <Alert
            variant="danger"
            className="org-activity-drawer__plan-expired"
            role="status"
          >
            <strong>PLAN EXPIRED</strong>
            {org?.currentPeriodEnd ? (
              <span> · {formatDaysFromNow(org.currentPeriodEnd)}</span>
            ) : null}
            <div className="small mt-1 mb-0">
              Prioritize renewal outreach for this organization.
            </div>
          </Alert>
        ) : null}
        {loading ? (
          <div className="d-flex align-items-center gap-2 text-muted">
            <Spinner animation="border" size="sm" />
            <span>Loading activity…</span>
          </div>
        ) : error ? (
          <Alert variant="danger" className="mb-0">
            {error}
          </Alert>
        ) : !data ? (
          <div className="text-muted small">No activity data.</div>
        ) : (
          <div className="org-activity-drawer__content">
            <section className="org-activity-drawer__section">
              <div className="org-activity-drawer__section-head">
                <h3>Overview</h3>
                <Button
                  size="sm"
                  variant="outline-secondary"
                  onClick={handleCopyBaseline}
                >
                  {copiedBaseline ? "Copied" : "Copy JSON baseline"}
                </Button>
              </div>
              <dl className="org-activity-drawer__meta">
                <div>
                  <dt>Status</dt>
                  <dd>
                    <Badge bg={data.active ? "success" : "secondary"}>
                      {data.active ? "Active" : "Inactive"}
                    </Badge>{" "}
                    <span className="text-muted small">
                      {data.subscriptionStatus ?? "—"}
                    </span>
                  </dd>
                </div>
                <div>
                  <dt>Created</dt>
                  <dd>{formatDateTime(data.createdAt)}</dd>
                </div>
                <div>
                  <dt>Plan</dt>
                  <dd>{data.usage.planName ?? "—"}</dd>
                </div>
                <div>
                  <dt>Contact</dt>
                  <dd>
                    {data.email || "—"}
                    {data.phone ? ` · ${data.phone}` : ""}
                  </dd>
                </div>
              </dl>
            </section>

            <section className="org-activity-drawer__section">
              <div className="org-activity-drawer__section-head">
                <h3>Send email</h3>
                <Button
                  size="sm"
                  variant="outline-secondary"
                  onClick={handleDraftFromActivity}
                >
                  Draft from activity
                </Button>
              </div>
              <p className="org-activity-drawer__hint">
                Plain-text outreach for marketing. Every email includes an
                unsubscribe link; suppressed addresses cannot be emailed again
                until cleared here.
              </p>
              {suppressed ? (
                <Alert variant="warning" className="py-2 small mb-2">
                  <div className="mb-2">
                    This address is suppressed (unsubscribed). Sending is blocked.
                  </div>
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    disabled={suppressionBusy}
                    onClick={handleClearSuppression}
                  >
                    {suppressionBusy ? "Working…" : "Allow emails again"}
                  </Button>
                </Alert>
              ) : null}
              {!data.email && !recipientEmail.trim() ? (
                <Alert variant="warning" className="py-2 small mb-2">
                  This organization has no email on file. Add a recipient below
                  to send.
                </Alert>
              ) : null}
              <Form onSubmit={handleSendEmail} className="org-activity-drawer__email-form">
                <Form.Group className="mb-2" controlId="org-email-to">
                  <Form.Label className="small mb-1">To</Form.Label>
                  <Form.Control
                    type="email"
                    size="sm"
                    value={recipientEmail}
                    onChange={(e) => {
                      setRecipientEmail(e.target.value);
                      void loadSuppression(e.target.value);
                    }}
                    placeholder="email@example.com"
                    required
                    disabled={suppressed}
                  />
                </Form.Group>
                <Form.Group className="mb-2" controlId="org-email-subject">
                  <Form.Label className="small mb-1">Subject</Form.Label>
                  <Form.Control
                    type="text"
                    size="sm"
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    placeholder="Subject"
                    maxLength={200}
                    required
                  />
                </Form.Group>
                <Form.Group className="mb-2" controlId="org-email-body">
                  <Form.Label className="small mb-1">Message</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={7}
                    size="sm"
                    value={body}
                    onChange={(e) => setBody(e.target.value)}
                    placeholder="Write the email body…"
                    required
                  />
                </Form.Group>
                {sendError ? (
                  <Alert variant="danger" className="py-2 small mb-2">
                    {sendError}
                  </Alert>
                ) : null}
                {sendSuccess ? (
                  <Alert variant="success" className="py-2 small mb-2">
                    {sendSuccess}
                  </Alert>
                ) : null}
                <div className="d-flex flex-wrap gap-2">
                  <Button
                    type="submit"
                    variant="primary"
                    size="sm"
                    disabled={!canSend}
                  >
                    {sendButtonLabel}
                  </Button>
                  {!suppressed && recipientEmail.trim() ? (
                    <Button
                      type="button"
                      variant="outline-secondary"
                      size="sm"
                      disabled={suppressionBusy}
                      onClick={handleSuppress}
                    >
                      Suppress address
                    </Button>
                  ) : null}
                </div>
              </Form>

              <div className="org-activity-drawer__email-history">
                <h4>Sent from panel</h4>
                {historyLoading ? (
                  <div className="text-muted small">Loading history…</div>
                ) : emailHistory.length === 0 ? (
                  <div className="text-muted small">No emails sent yet.</div>
                ) : (
                  <ul className="org-activity-drawer__log-list">
                    {emailHistory.map((item) => (
                      <li key={item.id ?? `${item.subject}-${item.createdAt}`}>
                        <div className="org-activity-drawer__log-top">
                          <span className="org-activity-drawer__log-when">
                            {formatDateTime(item.sentAt || item.createdAt)}
                          </span>
                          <Badge
                            bg={item.status === "sent" ? "success" : "secondary"}
                          >
                            {item.status ?? "—"}
                          </Badge>
                        </div>
                        <div className="org-activity-drawer__log-body">
                          <strong>{item.subject || "(no subject)"}</strong>
                          <div className="text-muted small">
                            {item.recipientEmail || "—"}
                            {item.purpose ? ` · ${item.purpose}` : ""}
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </section>

            <section className="org-activity-drawer__section">
              <h3>Entity counts</h3>
              <p className="org-activity-drawer__hint">
                How much data exists and when the first/last records were created.
              </p>
              <div className="table-responsive">
                <Table size="sm" className="mb-0 align-middle">
                  <thead>
                    <tr>
                      <th>Entity</th>
                      <th className="text-end">Count</th>
                      <th>First created</th>
                      <th>Last created</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ENTITY_ORDER.map((key) => {
                      const row = data.entities[key];
                      if (!row) return null;
                      return (
                        <tr key={key}>
                          <td>{ENTITY_LABELS[key] ?? key}</td>
                          <td className="text-end fw-semibold">{row.count}</td>
                          <td className="small text-muted">
                            {formatDateTime(row.firstCreatedAt)}
                          </td>
                          <td className="small text-muted">
                            {formatDateTime(row.lastCreatedAt)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>
              <div className="org-activity-drawer__usage">
                <span>
                  Products{" "}
                  {formatUsage(
                    data.usage.inventoryItems.current,
                    data.usage.inventoryItems.limit,
                  )}
                </span>
                <span>
                  Clients{" "}
                  {formatUsage(
                    data.usage.customers.current,
                    data.usage.customers.limit,
                  )}
                </span>
                <span>
                  Suppliers{" "}
                  {formatUsage(
                    data.usage.suppliers.current,
                    data.usage.suppliers.limit,
                  )}
                </span>
              </div>
            </section>

            <section className="org-activity-drawer__section">
              <h3>Agent signals</h3>
              <div className="org-activity-drawer__signals">
                {Object.entries(signals).map(([key, value]) => (
                  <Badge
                    key={key}
                    bg={value ? "success" : "secondary"}
                    className="org-activity-drawer__signal"
                  >
                    {key}: {value ? "yes" : "no"}
                  </Badge>
                ))}
              </div>
              <p className="org-activity-drawer__hint mb-0">
                {data.agentBaseline.purpose}
              </p>
            </section>

            <section className="org-activity-drawer__section">
              <h3>Recent activity logs</h3>
              <p className="org-activity-drawer__hint">
                Latest audit events
                {data.logsHasMore ? " (more available)" : ""}.
              </p>
              {data.recentLogs.length === 0 ? (
                <div className="text-muted small">No logs recorded yet.</div>
              ) : (
                <ul className="org-activity-drawer__log-list">
                  {data.recentLogs.map((log) => (
                    <li key={log.id ?? `${log.entityType}-${log.createdAt}`}>
                      <div className="org-activity-drawer__log-top">
                        <span className="org-activity-drawer__log-when">
                          {formatDateTime(log.createdAt)}
                        </span>
                        <Badge bg="light" text="dark">
                          {log.entityType ?? "—"} · {log.eventType ?? "—"}
                        </Badge>
                      </div>
                      <div className="org-activity-drawer__log-body">
                        {log.description ||
                          `${log.eventType ?? "event"} on ${log.entityType ?? "entity"} ${log.entityId ?? ""}`.trim()}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            <p className="org-activity-drawer__generated text-muted">
              Generated {formatDateTime(data.generatedAt)}
            </p>
          </div>
        )}
      </Offcanvas.Body>
    </Offcanvas>
  );
};
